import { confirmToast } from '@/common/toast';
import { useUpdateProblemApproval } from '@/hooks/diagnostic.hook';
import Editor from '@monaco-editor/react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { step1EditorOptions, step2EditableEditorOptions } from '../../utils/editorOptions';
import { getMonacoLanguage } from '../../utils/getMonacoLanguage';
import { handleSubmit } from '../../utils/submitHandlers';

/**
 * 훈련 상세 중앙 영역 - 에디터
 */
const TrainingDetailEditorArea = ({
  isApproval,
  problemId,
  activeFileName,
  activeFileStep,
  step,
  selectedLines,
  modifiedCode,
  fileContents,
  activeFile,
  onEditorMount,
  onEditorChange,
  minCenterWidth,
}) => {
  const [submitResult, setSubmitResult] = useState(null);
  const [step1TestPassed, setStep1TestPassed] = useState(false);
  const [step2TestPassed, setStep2TestPassed] = useState(false);
  const updateProblemApprovalMutation = useUpdateProblemApproval();
  const handleApprove = (problemId) => {
    confirmToast('해당 문제를 승인 상태로 전환하시겠습니까?', () => {
      updateProblemApprovalMutation.mutate({ problemId, approval: true });
    });
  };
  if (!activeFileName) {
    return (
      <Box sx={{ flex: 1, minWidth: minCenterWidth, display: 'flex', flexDirection: 'column' }}>
        <Paper sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            파일이 없습니다.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const editorValue = fileContents[activeFileName] || activeFile?.content || '';
  const editorOptions = activeFileStep === 1 ? step1EditorOptions : step2EditableEditorOptions;

  return (
    <Box sx={{ flex: 1, minWidth: minCenterWidth, display: 'flex', flexDirection: 'column' }}>

      <Paper sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight={500}>
            {activeFileName}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => handleSubmit(step, { problemId, selectedLines, modifiedCode, setSubmitResult, setStep1TestPassed, setStep2TestPassed })}
            disabled={(step === 1 && selectedLines.length === 0) || (step === 2 && modifiedCode.length === 0)}
          >
            채점
          </Button>
          {
            step1TestPassed === true && step2TestPassed === true && !isApproval && (
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={() => handleApprove(problemId)}
              >
                승인 상태로 전환
              </Button>
            )}
        </Box>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 500, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
        <Editor
          key={`${activeFileStep}-${activeFileName}`}
          height="100%"
          language={getMonacoLanguage(activeFileName)}
          theme="vs-light"
          {...(activeFileStep === 1
            ? { value: editorValue }
            : { defaultValue: editorValue })}
          onMount={onEditorMount}
          onChange={onEditorChange}
          options={editorOptions}
        />
      </Box>

      <Dialog
        open={submitResult !== null}
        onClose={() => setSubmitResult(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            textAlign: 'center',
            py: 1,
          },
        }}
      >
        <DialogContent>
          <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {submitResult ? (
              <>
                <Typography variant="h6" color="success.main" fontWeight={600}>
                  {submitResult?.correct ? '정답' : '오답'}
                </Typography>
              </>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button variant="contained" onClick={() => setSubmitResult(null)}>
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingDetailEditorArea;
